import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';
import API_URL from '../apiConfig';

const localizer = momentLocalizer(moment);

function AdminConsultationCalendar() {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchConsultations = async () => {
            try {
                const response = await fetch(`${API_URL}/consultation/get_all_sessions`);
                const sessions = await response.json();
                const events = sessions.map(session => ({
                    ...session,
                    title: session.title,
                    start: new Date(session.schedule),
                    end: new Date(session.schedule),
                    allDay: true,
                    agendaTitle: `Consultation: ${session.title}`,
                }));
                setEvents(events);
            } catch (error) {
                console.error('Error fetching admin consultations:', error);
            }
        };
        fetchConsultations();
    }, []);

    const eventPropGetter = (event, start, end, isSelected) => {
        return {
            style: {
                backgroundColor: '#057DCD',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                padding: '0.25rem',
                fontWeight: '100',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                transition: 'transform 0.2s',
                overflowY: 'auto',
                whiteSpace: 'normal',
            },
            onMouseOver: e => (e.currentTarget.style.transform = 'scale(1.02)'),
            onMouseOut: e => (e.currentTarget.style.transform = 'scale(1)'),
        };
    };

    const eventRenderer = ({ event }) => (
        <span>{event.agendaTitle || event.title}</span>
    );
    const monthEventRenderer = ({ event }) => (
        <span>{event.title}</span>
    );

    function CustomToolbar({ label, onNavigate, onView, view, views }) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#0065A8',
                color: 'white',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                gap: window.innerWidth < 768 ? '0.5rem' : '0'
            }}>
                {/* Navigation Buttons */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    order: window.innerWidth < 768 ? 2 : 1
                }}>
                    <button 
                        onClick={() => onNavigate('PREV')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: 'white',
                            fontSize: '1.25rem',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '0.25rem',
                            transition: 'all 0.3s ease',
                            marginRight: '0.5rem'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                    >◀</button>
                    <button 
                        onClick={() => onNavigate('TODAY')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: 'white',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            marginRight: '0.5rem',
                            fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                    >Today</button>
                    <button 
                        onClick={() => onNavigate('NEXT')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: 'white',
                            fontSize: '1.25rem',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '0.25rem',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                    >▶</button>
                </div>
                {/* Month/Year Label */}
                <span style={{ 
                    fontWeight: '600',
                    fontSize: window.innerWidth < 768 ? '1.25rem' : '1.5rem',
                    order: window.innerWidth < 768 ? 1 : 2,
                    textAlign: 'center'
                }}>{label}</span>
                {/* View Toggle Buttons */}
                <div style={{
                    order: window.innerWidth < 768 ? 3 : 3,
                    display: 'flex',
                    gap: '0.25rem'
                }}>
                    {views.map((v) => (
                        <button key={v}
                            onClick={() => onView(v)}
                            style={{
                                background: view === v ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.1)",
                                border: "none",
                                color: "white",
                                padding: "0.5rem 1rem",
                                borderRadius: "0.25rem",
                                cursor: "pointer",
                                transition: 'all 0.3s ease',
                                fontWeight: view === v ? '600' : '400',
                                fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem'
                            }}
                            onMouseOver={(e) => {
                                if (view !== v) {
                                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (view !== v) {
                                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                }
                            }}
                        >
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100vh', padding: '1rem', backgroundColor: '#f8fafc' }}>
            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'calc(100vh - 2rem)', width: '100%', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                views={['month', 'agenda']}
                defaultView="month"
                formats={{ eventTimeRangeFormat: () => '', dayFormat: 'ddd', dayHeaderFormat: 'ddd M/D' }}
                components={{ toolbar: CustomToolbar, month: { event: monthEventRenderer }, agenda: { event: eventRenderer } }}
                eventPropGetter={eventPropGetter}
                popup={true}
                popupOffset={30}
                onSelectEvent={event => {
                    setSelectedEvent(event);
                    setShowModal(true);
                }}
            />

            {/* Modal for Consultation Info */}
            {showModal && selectedEvent && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}
                    onClick={() => setShowModal(false)}
                >
                    <div style={{
                        background: 'white',
                        borderRadius: '0.75rem',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                        padding: '2rem',
                        minWidth: '500px',
                        maxWidth: '90vw',
                        position: 'relative',
                        cursor: 'auto'
                    }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: '#057DCD',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.375rem',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                            onClick={() => setShowModal(false)}
                        >Close</button>
                        <h2 style={{ color: '#057DCD', marginBottom: '1rem', paddingBottom: '0.5rem', paddingRight: '2rem' }}>Consultation Information</h2>
                        <div style={{ marginBottom: '0.5rem' }}><strong>Teacher:</strong> {selectedEvent.title}</div>
                        <div style={{ marginBottom: '0.5rem' }}><strong>Date & Time:</strong> {selectedEvent.schedule ? moment(selectedEvent.schedule).format('MMMM D, YYYY h:mm A') : moment(selectedEvent.start).format('MMMM D, YYYY h:mm A')}</div>
                        {selectedEvent.description && <div style={{ marginBottom: '0.5rem' }}><strong>Description:</strong> {selectedEvent.description}</div>}
                        {selectedEvent.location && <div style={{ marginBottom: '0.5rem' }}><strong>Location:</strong> {selectedEvent.location}</div>}
                        {/* Add more fields as needed */}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminConsultationCalendar;
